import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import StandardButton from '../components/StandardButton';
import CreditCardProvider from './CreditCardProvider';
import { setPaymentFormInUse } from '../slices/paymentFormInUse';
import SavedCardProvider from './SavedCardProvider';
import ApplePayProvider from './ApplePayProvider';
import AppleGooglePayButtons from './AppleGooglePayButtons';
import { useFormikContext } from 'formik';
import GooglePayProvider from './GooglePayProvider';
import { OrderingConstants, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import { setPaymentMethod } from '../slices/paymentMethod';
import GiftcardSurcharge from './GiftcardSurcharge';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import combineStyles from '../utils/combineStyles';
import ReCAPTCHA from 'react-google-recaptcha';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { setGoogleReCaptcha } from '../slices/googleReCaptcha';
import { useTyro } from '../hooks/TyroProvider';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { GIFTCARD_PURCHASE_SUCCESSFUL_MODAL } from '../modals/GiftcardSuccessModal';
import { setPaymentFormFilled } from '../slices/paymentFormFilled';
import { purchaseGiftCard } from '../utils/analytics';
import PaymentWrapper from './PaymentProvider';

const {
  getMember,
  getGatewayType,
  getCalculatedGiftcardSurcharge,
  getTyroPaymentStatus,
  getPaymentGatewayTokens,
  getEnableDynamicPaymentGatewayConfig,
  getLocation,
  getSaleType,
} = OrderingSelectors;

const {
  tyroSale,
  setTyroPaymentStatus,
  fetchPaymentGatewayConfig,
  fetchPaymentGatewayTokens,
  fetchMember,
} = OrderingOperations;
const { PAYMENT_GATEWAY_TYPES, TYRO_PAYMENT_TOKEN_STATUS, PAYMENT_METHOD } = OrderingConstants;

const SuperPaymentWrapper = ({ children }: { children: React.ReactNode }) => {
  const gatewayType = useAppSelector(getGatewayType);
  // eslint-disable-next-line
  const tyroData = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? useTyro() : null;
  const { tyroJSRef } = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && tyroData;

  return (
    <>
      {gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE && children}
      {gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && (
        <div ref={tyroJSRef}>
          <form id="pay-form">{children}</form>
        </div>
      )}
    </>
  );
};

const SuperPaymentForm = ({
  route,
  notRequired = [],
}: {
  route: string;
  notRequired?: string[];
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const gatewayType = useAppSelector(getGatewayType);
  const paymentFormFilled = useAppSelector(state => state.paymentFormFilled);
  const paymentFormInUse = useAppSelector(state => state.paymentFormInUse);
  const paymentMethod = useAppSelector(state => state.paymentMethod);
  const giftCardTopUpLocationId = useAppSelector(state => state.config.giftCardTopUpLocationId);
  const enableDynamicPaymentGatewayConfig = useAppSelector(getEnableDynamicPaymentGatewayConfig);
  const location = useAppSelector(getLocation);
  const saleType = useAppSelector(getSaleType);

  // Here for some reason stripe uses savedCards instead of getPaymentGatewayTokens on gift card/top up screen so just keeping that
  const savedCardsStripe = useAppSelector(state => state.savedCards);
  const savedCardsTyro = useAppSelector(getPaymentGatewayTokens);
  const savedCards =
    gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? savedCardsStripe : savedCardsTyro;

  const enableReCaptcha = useAppSelector(state => state.ordering.config.enableReCaptcha);
  const reCaptchaSiteKey = useAppSelector(state => state.config.reCaptchaSiteKey);
  const reCaptchaToken = useAppSelector(state => state.googleReCaptcha);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const enableReCaptchaForMemberTopUp = useAppSelector(
    state => state.config.enableReCaptchaForMemberTopUp,
  );
  const enableReCaptchaForGiftCard = useAppSelector(
    state => state.config.enableReCaptchaForGiftCard,
  );
  const tyroPaymentStatus = useAppSelector(getTyroPaymentStatus);
  const tyroPollingRules = useAppSelector(state => state.config.tyroPollingRules);

  // eslint-disable-next-line
  const tyroData = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? useTyro() : null;
  const {
    giftCardTopUpData,
    setGiftCardTopUpData,
    tyroPaymentOrderId,
    setTyroPaymentOrderId,
    setExternalDependency,
    hasPaySecret,
    payRequestReady,
    showAppleGooglePayBtn,
    setShowAppleGooglePayBtn,
  } = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && tyroData;

  //reset payment method on mount
  useEffect(() => {
    if (paymentMethod) {
      dispatch(setPaymentMethod(null));
    }

    if (enableDynamicPaymentGatewayConfig) {
      dispatch(fetchPaymentGatewayConfig({ locationId: giftCardTopUpLocationId as string }));
    }
  }, []);

  const member = useAppSelector(getMember);

  const formik = useFormikContext<FormikFields>();

  const surcharge = useAppSelector(state =>
    getCalculatedGiftcardSurcharge(state, Number(formik?.values?.Amount || 0)),
  );

  // check whether the form is filled and error free then disable
  const formDisabled =
    !formik.isValid ||
    (formik?.values?.Amount && formik.values.Amount <= 0) ||
    formik.isSubmitting ||
    Object.entries(formik.values)
      .filter(pair => !notRequired.includes(pair[0]))
      .some(pair => pair[1] === '') ||
    !paymentMethod;

  const payingWithGoogleOrApplePay =
    paymentMethod?.method === PAYMENT_METHOD.GOOGLE_PAY ||
    paymentMethod?.method === PAYMENT_METHOD.APPLE_PAY;

  const getButtonLabel = () => {
    if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && payingWithGoogleOrApplePay) {
      return t('button.continue');
    }

    if (route === 'giftCardTopUp' || route === 'memberCashTopUp') {
      return t('button.topupButton');
    }

    return t('button.purchaseButton');
  };

  const buttonLabel = getButtonLabel();

  const giftcardRoutes = [
    'memberPurchaseGiftCardForOthers',
    'nonMemberPurchaseGiftCardForOthers',
    'memberPurchaseGiftCardForSelf',
    'nonMemberPurchaseGiftCardForSelf',
  ];

  const determineReCaptcha = () => {
    if (route === 'memberCashTopUp' && enableReCaptchaForMemberTopUp) {
      return true;
    } else if ([...giftcardRoutes, 'giftCardTopUp'].includes(route) && enableReCaptchaForGiftCard) {
      return true;
    } else if (
      !['memberCashTopUp', 'giftCardTopUp', ...giftcardRoutes].includes(route) &&
      enableReCaptcha
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
      if (formik?.values?.Amount && formik.values.Amount > 0) {
        setGiftCardTopUpData({
          ...giftCardTopUpData,
          values: formik?.values,
          route,
          giftcardSurcharge: surcharge.value,
        });
      } else {
        setGiftCardTopUpData(null);
      }
    }
  }, [formik?.values, surcharge]);

  const fetchPaymentStatusInterval = useRef<NodeJS.Timeout | null>(null);
  const readyInterval = useRef<NodeJS.Timeout | null>(null);
  const paymentStatusPollCount = useRef(0);

  const fetchTokenStatus = () => {
    if (!tyroPaymentOrderId.length) {
      return;
    }

    // Stop polling if max polling count hits
    if (paymentStatusPollCount.current >= tyroPollingRules.maxCount) {
      clearInterval(fetchPaymentStatusInterval.current as NodeJS.Timeout);
      enqueueErrorSnackbar('Payment polling status check timed out. Please try again.');
      dispatch(setPaymentFormInUse(false));
      setTyroPaymentOrderId('');
      dispatch(setTyroPaymentStatus(null));
      return;
    }

    if (
      !tyroPaymentStatus ||
      (tyroPaymentStatus &&
        (tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.PENDING ||
          tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.PROCESSING))
    ) {
      dispatch(
        tyroSale({
          orderId: tyroPaymentOrderId,
          authenticationMethod: member ? 'member' : 'none',
          shouldSetSaleDetails: false,
        }),
      );
    } else {
      clearInterval(fetchPaymentStatusInterval.current as NodeJS.Timeout);
    }

    // Incrementing polling count
    paymentStatusPollCount.current += 1;
  };

  useEffect(() => {
    // Polling tyro payment status from BE
    if (
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
      paymentFormInUse &&
      tyroPaymentOrderId.length
    ) {
      fetchPaymentStatusInterval.current = setInterval(fetchTokenStatus, tyroPollingRules.interval);

      fetchTokenStatus();

      return () => {
        clearInterval(fetchPaymentStatusInterval.current as NodeJS.Timeout);
        clearInterval(readyInterval.current as NodeJS.Timeout);
      };
    }
  }, [gatewayType, paymentFormInUse, tyroPaymentOrderId]);

  useEffect(() => {
    if (tyroPaymentStatus) {
      if (tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.SUCCESS) {
        dispatch(fetchPaymentGatewayTokens({}));

        if (route === 'memberCashTopUp') {
          dispatch(fetchMember({ authenticationMethod: 'member' }));
        }

        if (giftcardRoutes.includes(route)) {
          const isGift = Boolean(formik.values.RecipientEmail);
          const purchaserEmail = formik.values.Email ?? member?.email ?? '';
          const recipientEmail = isGift ? formik.values.RecipientEmail! : purchaserEmail;
          const scheduled =
            isGift && Boolean(formik.values.delivery) && Boolean(formik.values.SendTime);

          dispatch(
            setCurrentModal({
              modalId: GIFTCARD_PURCHASE_SUCCESSFUL_MODAL,
              params: {
                giftcard: tyroPaymentStatus.Data as any,
                isGift,
                purchaserEmail,
                recipientEmail,
                scheduled,
                sendTime: formik.values.SendTime,
              },
            }),
          );
        } else {
          enqueueSuccessSnackbar(tyroPaymentStatus.Data.msg || 'Success');
        }
        formik.resetForm();
        dispatch(setPaymentFormInUse(false));
        dispatch(setPaymentMethod(null));
        dispatch(setPaymentFormFilled(false));
        dispatch(setGoogleReCaptcha(null));
        if (giftcardRoutes.includes(route)) {
          purchaseGiftCard({
            amount: tyroPaymentStatus.Data.Amount ?? 0,
            store: location?.name,
            saleType,
          });
        }

        setTyroPaymentOrderId('');
        dispatch(setTyroPaymentStatus(null));
      } else if (
        tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.FAILED ||
        tyroPaymentStatus.Status === TYRO_PAYMENT_TOKEN_STATUS.VOIDED
      ) {
        enqueueErrorSnackbar(tyroPaymentStatus.Data.error || `Something went wrong`);
        dispatch(setPaymentFormInUse(false));
        setTyroPaymentOrderId('');
        dispatch(setTyroPaymentStatus(null));
      }
    }
  }, [tyroPaymentStatus]);

  // Initially showAppleGooglePayBtn is false, when user fills all the details in the form and click on purchase button
  // we set the showAppleGooglePayBtn to true so that we can display the google/apple pay button but after that
  // if something changes in the form we are setting showAppleGooglePayBtn back to false here
  // so that when they click back on purchase button we regenerate the pay secret with latest form data
  useEffect(() => {
    if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && showAppleGooglePayBtn) {
      setShowAppleGooglePayBtn(false);
    }
  }, [formik?.values, paymentMethod]);

  /* 
  For credit card payment we need to generate the card when we have any amount at the start 
  but after that if the amount changes we can handle that with validate gift card 
  before making the payment to tyro
  */
  useEffect(() => {
    const shouldRegeneratePaySecretForCreditCard = !hasPaySecret;

    if (
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
      paymentMethod?.method === PAYMENT_METHOD.CREDIT_CARD &&
      formik?.values?.Amount &&
      shouldRegeneratePaySecretForCreditCard
    ) {
      setExternalDependency({
        values: formik?.values,
        route,
        giftcardSurcharge: surcharge.value,
      });
    }
  }, [formik?.values?.Amount]);

  const reCaptchaSatisfied = !(determineReCaptcha() && !reCaptchaToken);

  const displayAppleGooglePayBtn =
    !formDisabled &&
    reCaptchaSatisfied &&
    (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
      ? showAppleGooglePayBtn
      : payingWithGoogleOrApplePay);

  const SavedCardsList = (
    <>
      {member &&
        savedCards?.map((savedCard: PaymentGateWayToken) => (
          <SavedCardProvider key={savedCard.token} savedCard={savedCard} route={route} />
        ))}
    </>
  );

  return (
    <SuperPaymentWrapper>
      {gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? (
        <PaymentWrapper shouldHaveMerchantAccount>{SavedCardsList}</PaymentWrapper>
      ) : (
        SavedCardsList
      )}

      <CreditCardProvider route={route} />
      <ApplePayProvider />
      <GooglePayProvider />

      {giftcardRoutes.includes(route) && <GiftcardSurcharge />}

      <div
        style={combineStyles({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          marginTop: 15,
          justifyContent: 'center',
        })}
      >
        {determineReCaptcha() && !reCaptchaToken ? (
          <ReCAPTCHA
            sitekey={reCaptchaSiteKey!}
            onChange={token => {
              dispatch(setGoogleReCaptcha(token));
            }}
            onErrored={() => {
              setGoogleReCaptcha(null);
              enqueueErrorSnackbar(t('problemWithReCaptchaMessage'));
            }}
            size={deviceTypeMobile ? 'compact' : 'normal'}
          />
        ) : null}
      </div>

      {displayAppleGooglePayBtn && (
        <div style={{ marginTop: 20 }}>
          {gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? (
            <PaymentWrapper shouldHaveMerchantAccount>
              <AppleGooglePayButtons
                route={route}
                containerStyle={styles.button}
                label={buttonLabel}
                paymentStatusPolling={
                  tyroPaymentOrderId && paymentStatusPollCount.current < tyroPollingRules.maxCount
                }
              />
            </PaymentWrapper>
          ) : (
            <AppleGooglePayButtons
              route={route}
              containerStyle={styles.button}
              label={buttonLabel}
              paymentStatusPolling={
                tyroPaymentOrderId && paymentStatusPollCount.current < tyroPollingRules.maxCount
              }
            />
          )}
        </div>
      )}

      {!displayAppleGooglePayBtn && (
        <div
          style={{
            marginTop: 20,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <StandardButton
            disabled={
              formDisabled ||
              paymentFormInUse ||
              (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE && !paymentFormFilled) ||
              // Only the Tyro card form needs a pay secret up front. Saved cards use their own flow and wallet pay fetches its secret after this button is clicked
              (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
                paymentMethod?.method === PAYMENT_METHOD.CREDIT_CARD &&
                !payRequestReady) ||
              (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
                tyroPaymentOrderId &&
                paymentStatusPollCount.current < tyroPollingRules.maxCount) ||
              (determineReCaptcha() && !reCaptchaToken)
            }
            onClick={() => {
              if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && payingWithGoogleOrApplePay) {
                setExternalDependency({
                  values: formik?.values,
                  route,
                  giftcardSurcharge: surcharge.value,
                });
                setShowAppleGooglePayBtn(true);
              } else {
                dispatch(setPaymentFormInUse(true));
              }
            }}
            themeKey="loyaltyButton"
            containerStyle={styles.button}
            label={buttonLabel}
            showSpinner={paymentFormInUse}
          />
        </div>
      )}
    </SuperPaymentWrapper>
  );
};

export default SuperPaymentForm;

const styles = {
  button: {
    borderRadius: 5,
    height: 40,
    width: '100%',
  },
};
